import React from "react";
import { Trans } from "react-i18next/icu.macro";

import {
  FetchNotFoundError,
  FetchServerError,
  FetchUnauthorizedError,
  FetchPasswordInvalidError,
  FetchSsoError,
  FetchElevatedAccessError,
} from "@lib/errors";
import { UNIDENTIFIED_ERROR } from "@lib/fetch";

import useI18nNamespace from "@services/useI18nNamespace";

import { getBrazeApiDenialCode } from "./utils/getBrazeApiDenialCode";
import type { FetchErrorModalBodyProps } from "./types";

/**
 * Error-specific custom modal body to inform the user of what error has occurred.
 */
export const FetchErrorModalBody = ({ error }: FetchErrorModalBodyProps): React.ReactElement => {
  const { t, k } = useI18nNamespace("server-fetch-error-messages");
  const denialCode = getBrazeApiDenialCode(error);

  // Braze::API denials carry a machine-readable error_code; per the API standards the frontend
  // renders localized copy from the code rather than the envelope's English-fallback message.
  // These run before the class branches so a refined denial is never mistaken for the generic
  // permissions / timeout case. Legacy endpoints never emit the envelope, so their treatment
  // cannot change.
  switch (denialCode) {
    case "forbidden.csrf":
      return (
        <Trans i18nKey={k("csrf-token-expired-body")}>
          Your session token expired. Refresh the page and try again.
        </Trans>
      );
    case "forbidden.read_only_impersonation":
      return (
        <Trans i18nKey={k("read-only-impersonation-body")}>
          You are impersonating in read-only mode, so this action is unavailable.
        </Trans>
      );
    case "unauthorized.ip_not_allowed":
    case "unauthorized.company_banned":
      return (
        <Trans i18nKey={k("account-restricted-body")}>
          There is something wrong with your account. Please contact your Account Administrator.
        </Trans>
      );
    // The page called a route that does not accept a dashboard session. Only an engineer can fix
    // that, so the copy does not ask the user to sign in again.
    case "unauthorized.credential_type_not_accepted":
      return (
        <Trans i18nKey={k("credential-type-not-accepted-body")}>
          This page could not load some of its data. Signing in again does not fix it. If you keep seeing this
          message, contact <a href="https://www.braze.com/docs/support_contact">support</a>.
        </Trans>
      );
    case "sandbox_tos.required":
      return (
        <Trans i18nKey={k("sandbox-tos-required-body")}>
          Please refresh the page to review and accept the sandbox Terms of Service.
        </Trans>
      );
    case "service_unavailable.company":
      return (
        <Trans i18nKey={k("account-unavailable-body")}>
          This account is temporarily unavailable. Please try again shortly. If you continue to receive this error
          contact <a href="https://www.braze.com/docs/support_contact">support</a> directly.
        </Trans>
      );
  }

  if (error instanceof FetchServerError) {
    switch (error.status) {
      case 503:
      case 504:
        return (
          <Trans i18nKey={k("request-timeout-body")}>
            Your request timed out. Please try refreshing the page. If you continue to receive this error contact{" "}
            <a href="https://www.braze.com/docs/support_contact">support</a> directly.
          </Trans>
        );
      case 500:
        return (
          <>
            {error.message === "An unexpected error has occurred"
              ? t("generic-unexpected-error-body", "An unexpected error has occurred")
              : error.message}
          </>
        );
    }
  }
  if (error instanceof FetchElevatedAccessError) {
    return (
      <Trans i18nKey={k("fetch-elevated-access-error-body")}>
        This page has heightened security requirements and reauthentication is required. Please refresh your browser
        and try again.
      </Trans>
    );
  }
  if (error instanceof FetchNotFoundError) {
    return (
      <Trans i18nKey={k("fetch-not-found-body")}>
        Oops! It looks like we made a mistake. The page you requested could not be found.
        <br />
        <br />
        If you need further assistance, contact <a href="https://www.braze.com/docs/support_contact">support</a>
        directly. When contacting support, please include any details you can to help reproduce the error.
      </Trans>
    );
  }
  if (error instanceof FetchUnauthorizedError) {
    return (
      <Trans i18nKey={k("unauthorized-body")}>
        Oops! You don&apos;t have the permissions for the requested action.
        <br />
        <br />
        Contact your company&apos;s administrator to request access or change your permissions.
      </Trans>
    );
  }
  if (error instanceof FetchPasswordInvalidError) {
    return (
      <Trans i18nKey={k("password-expired-or-invalid-body")}>
        Your administrator has updated your password requirements, or your password has expired.
        <br />
        <br />
        Please refresh the page to update your password.
      </Trans>
    );
  }
  if (error instanceof FetchSsoError) {
    return (
      <Trans i18nKey={k("sso-body")}>
        Your administrator now requires SSO authentication.
        <br />
        <br />
        Please logout then login using a Single Sign-On provider.
      </Trans>
    );
  }
  // This refresh copy is for ApplicationController#render_409, which a reload fixes. Many legacy endpoints also
  // send a JSON 409, so every 409 without the Braze::API envelope keeps this copy. A Braze::API 409 carries a
  // domain error_code that a reload cannot fix, so it gets the generic copy below, like an enveloped 400 or 422.
  if (error.status === UNIDENTIFIED_ERROR && denialCode === undefined) {
    return (
      <Trans i18nKey={k("unidentified-error-body")}>
        Oops! An unspecified error has occurred. Refresh your page and everything should be fine! If you keep seeing
        this message, please reach out to support.
      </Trans>
    );
  }
  // Regular errors show their own message. extractError's generic message gets the localized copy, as a 500 does.
  return (
    <>
      {error.message === "An unexpected error has occurred"
        ? t("generic-unexpected-error-body", "An unexpected error has occurred")
        : error.message}
    </>
  );
};
